import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, TrendingUp, Award, Zap } from "lucide-react";

const TIER_COLORS = {
  bronze: { bg: "from-amber-700 to-amber-900", text: "text-amber-100", badge: "#CD7F32" },
  silver: { bg: "from-slate-400 to-slate-600", text: "text-slate-100", badge: "#C0C0C0" },
  gold:   { bg: "from-yellow-500 to-amber-600", text: "text-asap-deep", badge: "#FFD700" },
};

export default function DriverTierCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/driver/tier");
        setData(data);
      } catch {}
    };
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  const tierId = data.tier.id;
  const colors = TIER_COLORS[tierId] || TIER_COLORS.bronze;

  // Progress to next tier
  const nextJobs = data.next_tier?.min_jobs;
  const prevMin = nextJobs ? Math.max(0, data.completed_jobs - data.jobs_to_next) : data.completed_jobs;
  const totalSpan = nextJobs ? nextJobs - (prevMin - (data.jobs_to_next > 0 ? data.jobs_to_next - (nextJobs - prevMin) : 0)) : 1;
  const progressPct = nextJobs ? Math.min(100, Math.max(0, ((data.completed_jobs - prevMin + (totalSpan - (nextJobs - data.completed_jobs))) / totalSpan) * 100)) : 100;

  return (
    <div className={`bg-gradient-to-br ${colors.bg} border-2 border-asap-yellow p-5 relative overflow-hidden`} data-testid="driver-tier-card">
      <div className="absolute -right-6 -top-6 opacity-20 rotate-12">
        <Trophy size={140} />
      </div>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full grid place-items-center" style={{ background: colors.badge, color: "#061224" }}>
          <Award size={26} strokeWidth={2.5}/>
        </div>
        <div>
          <div className={`text-[11px] tracking-[0.3em] uppercase font-bold ${colors.text} opacity-80`}>Driver Tier</div>
          <div className={`font-display text-3xl uppercase ${colors.text}`} data-testid="driver-tier-name">{data.tier.name}</div>
        </div>
        <div className="ml-auto text-right">
          <div className={`text-[10px] uppercase tracking-widest font-bold ${colors.text} opacity-80`}>Pay Multiplier</div>
          <div className={`font-display text-2xl ${colors.text}`} data-testid="tier-multiplier">{data.tier.multiplier.toFixed(2)}×</div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 text-center">
        <Stat label="Completed" value={data.completed_jobs} text={colors.text}/>
        <Stat label="Rating" value={`${data.rating.toFixed(1)}★`} text={colors.text} sub={data.ratings_count ? `${data.ratings_count} review${data.ratings_count === 1 ? "" : "s"}` : "no reviews yet"}/>
        <Stat label="Earnings" value={`$${data.total_earnings.toFixed(0)}`} text={colors.text}/>
      </div>

      {data.next_tier ? (
        <div className="mt-4">
          <div className={`flex justify-between text-[11px] uppercase tracking-widest font-bold ${colors.text}`}>
            <span>Next: {data.next_tier.name} ({data.next_tier.multiplier}×)</span>
            <span>{data.jobs_to_next} more jobs</span>
          </div>
          <div className="mt-1 h-2 bg-black/30 rounded">
            <div className="h-full rounded transition-all" style={{ width: `${progressPct}%`, background: data.next_tier.color || "#FFD700" }}/>
          </div>
        </div>
      ) : (
        <div className={`mt-4 inline-flex items-center gap-2 text-sm ${colors.text} font-bold`}><Zap size={16} className="text-asap-yellow"/> Top tier achieved!</div>
      )}
    </div>
  );
}

function Stat({ label, value, text, sub }) {
  return (
    <div className="bg-black/30 rounded p-2">
      <div className={`text-[10px] uppercase tracking-widest font-bold ${text} opacity-80`}>{label}</div>
      <div className={`font-display text-xl ${text}`}>{value}</div>
      {sub && <div className={`text-[9px] tracking-widest uppercase ${text} opacity-60 mt-0.5`}>{sub}</div>}
    </div>
  );
}
